import { useLocation, useNavigate, useParams } from "react-router-dom";
import addressApi, {
  MUTATION_SYNC_PROPERTY_BY_ID,
  QUERY_SYNC_PROPERTY_BY_ID_STATUS
} from "api/landlord/address";
import { useState, useEffect, useRef } from "react";
import { getErrorMessage } from "utils/error";
import { SingleSyncPropertyManagementSystems } from "utils/singleSyncPMS";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { Plus } from "untitledui-js-base";
import { Layout } from "providers/Layout";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { DeletePropertyModal } from "shared/app/DeletePropertyModal";
import { useAuthContext } from "providers/Auth";
import { UnitsTableView } from "shared/app/UnitsTableView/index";
import { ApplicationsTableView } from "shared/app/ApplicationsTableView/index";
import { Loader } from "shared/components/Loader/index";
import { Tabs } from "shared/components/BaseTabs";
import { Property } from "types/landlord/address";
import { Redirect } from "shared/components/Redirect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  PropertyManagementSystemSyncStatus,
  PropertyManagementSystem
} from "shared/types";

import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useUnitAvailabilityPermission } from "shared/hooks/use-unit-availability-permission";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { PropertyKind } from "shared/types";
import { DiscountCodesTab } from "../properties-id-units-id/components/DiscountCodesTab";
import { useSettingsController } from "../settings-integration/controller";
import { LayoutHeader } from "./components/LayoutHeader";
import { LayoutFooter } from "./components/LayoutFooter";
import { PropertyDetails } from "./components/PropertyDetails";
import { LeadsTableView } from "shared/app/LeadsTableView";
import { ShowingType } from "api/scheduling/types";
import { ShowingRotation } from "./components/ShowingRotation/ShowingRotation";
import { PropertyActivityLog } from "./slices/ActivityLog";
import { useRevealSectionFromUrl } from "shared/app/ListingHealth/navigation";

const getTabs = (
  type: ReturnType<typeof useAuthContext>["type"],
  dictionary: ReturnType<typeof useUnitsPropertiesDictionary>
) => {
  const tabs: string[] = [`${dictionary.Property} details`];

  // Show the Units tab for every property kind, including Single Family.
  // SF used to hide it, which left its (self-)unit detail page — where the
  // CRM-862 Showing availability tab lives — reachable only via the global
  // Units list. Now SF reaches its unit the same way every other kind does
  // (CRM-989).
  tabs.push(dictionary.Units);

  // Each CRM tab follows its own entitlement, not just the umbrella flag: a
  // Core partner with leads or showings revoked keeps the property page but
  // loses the surfaces it cannot use (CRM-759).
  if (type.isBoomCRM && type.isTenantLeads) tabs.push("Leads");

  if (type.isBoomCRM && type.isShowings) tabs.push("Showing rotation");

  if (type.isBoomScreen) tabs.push("Applications");

  if (type.isBoomScreen) tabs.push("Discount codes");

  return tabs;
};

export const PropertyDetail = () => {
  const location = useLocation();
  const { isEverySyncEnabled } = useSettingsController();
  const dictionary = useUnitsPropertiesDictionary();
  const { type, partner } = useAuthContext();
  const { canChangeUnitAvailability, unitAvailabilityDeniedTooltip } =
    useUnitAvailabilityPermission();
  const queryClient = useQueryClient();
  const { propertyId: propertyIdParam } = useParams();

  const isSingleSyncSupported = SingleSyncPropertyManagementSystems.includes(
    partner?.property_management_system?.name as PropertyManagementSystem
  );
  const navigate = useNavigate();

  // MobileView
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [propertyId, setpropertyId] = useState<string | null>(null);

  const propertyMeta = useQuery({
    queryKey: ["properties", propertyIdParam],
    queryFn: () => addressApi.getProperty(propertyIdParam || "")
  });

  const property: Property | undefined = propertyMeta.data;

  const { defaultTab } = location.state || {};

  const [tab, setActiveTab] = useState(
    defaultTab || getTabs(type, dictionary)[0]
  );

  useRevealSectionFromUrl(
    Boolean(property) && tab === `${dictionary.Property} details`
  );

  useEffect(() => {
    if (propertyMeta.isSuccess && propertyMeta.data?.id)
      setpropertyId(propertyMeta.data.id);
  }, [propertyMeta.isSuccess, propertyMeta.data]);

  const { mutate, isPending } = useMutation({
    mutationKey: ["turnOnOffLinks", propertyId],
    mutationFn: (active: boolean) => {
      return addressApi.updateAllLinks(propertyId || "", active);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/portal/v1/properties/${propertyId}/units/by_name`]
      });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const [isSyncing, setIsSyncing] = useState(type.isPMS);

  const { data: syncData, isError: syncError } =
    useQuery<PropertyManagementSystemSyncStatus>({
      queryKey: [QUERY_SYNC_PROPERTY_BY_ID_STATUS, propertyIdParam],
      queryFn: () =>
        addressApi.getPropertyManagementSystemSyncByIdStatus(
          "properties",
          propertyIdParam || ""
        ),
      refetchInterval: 3000,
      enabled: isSyncing && !!propertyIdParam
    });

  const { mutate: handleRequestSync } = useMutation({
    mutationFn: async () => {
      return addressApi
        .requestPropertyManagementSystemSyncById(
          "properties",
          propertyIdParam || ""
        )
        .then(() => {
          toast.success("Sync property requested successfully");
          setIsSyncing(true);
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
          setIsSyncing(false);
        });
    },
    mutationKey: [MUTATION_SYNC_PROPERTY_BY_ID, propertyIdParam]
  });

  useEffect(() => {
    setIsSyncing(type.isPMS);
  }, [type.isPMS]);

  // Only refetch the property on the running → completed transition (once),
  // not on every poll tick. Avoids dragging the heavy property GET into the
  // background while the user is editing.
  const wasSyncRunningRef = useRef(false);
  useEffect(() => {
    const isSyncRunning = syncData?.status === "running";
    setIsSyncing(isSyncRunning);

    if (wasSyncRunningRef.current && !isSyncRunning) propertyMeta.refetch();
    wasSyncRunningRef.current = isSyncRunning;
  }, [syncData, syncError]);

  if (type.isSDK && !type.isBoomScreen) return <Redirect href="/" />;

  if (propertyMeta.isLoading)
    return (
      <Layout>
        <div className="flex items-center justify-center w-full h-full">
          <Loader />
        </div>
      </Layout>
    );

  if (!property || propertyMeta.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading {dictionary.property}.
          </h1>
          <p className="text-base font-medium">{propertyMeta.error?.message}</p>
        </div>
      </Layout>
    );

  const propertyName =
    property.name || getShortAddressFromAddress(property.address) || "";
  const allLinksTooltip =
    unitAvailabilityDeniedTooltip ??
    (isEverySyncEnabled
      ? "The settings has already been automatically synchronized for all partners. (Settings/integration)"
      : "");
  const allLinksTooltipPlacement = unitAvailabilityDeniedTooltip
    ? "top-start"
    : undefined;
  const tabs = getTabs(type, dictionary);

  const componentsMap = {
    [`${dictionary.Property} details`]: () => (
      <PropertyDetails property={property} />
    ),
    [dictionary.Units]: () => (
      <UnitsTableView
        property={property}
        query={`/portal/v1/properties/${propertyId}/units/by_name`}
        propertyId={Number(propertyId)}
        hideSync={isSingleSyncSupported}
        buttons={
          <div className="flex w-full gap-2 md:w-auto">
            <Show if={type.isBoomScreen}>
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.BASE}
                onClick={() => mutate(true)}
                loading={isPending}
                disabled={isEverySyncEnabled || !canChangeUnitAvailability}
                tooltip={allLinksTooltip}
                tooltipPlacement={allLinksTooltipPlacement}
                className="w-full md:w-auto"
              >
                Allow all
              </Button>
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.BASE}
                onClick={() => mutate(false)}
                loading={isPending}
                disabled={isEverySyncEnabled || !canChangeUnitAvailability}
                tooltip={allLinksTooltip}
                tooltipPlacement={allLinksTooltipPlacement}
                className="w-full md:w-auto"
              >
                Disallow all
              </Button>
            </Show>
            {type.isPMS ||
            type.isPropertyManager ||
            // Single Family is one self-unit by definition, so it never gets
            // more units added — consistent with AddUnitModal, which also
            // skips SF properties (CRM-989).
            property.kind === PropertyKind.SingleFamily ? null : (
              <Button
                size={ButtonSize.SM}
                variant={ButtonVariant.PRIMARY}
                onClick={() =>
                  navigate(`/properties/${propertyId}/units/add`, {
                    state: {
                      property
                    }
                  })
                }
                Icon={Plus}
              >
                Add {dictionary.Units}
              </Button>
            )}
          </div>
        }
      />
    ),
    ["Leads"]: () => (
      <LeadsTableView
        property_ids={propertyIdParam ? [String(propertyIdParam)] : []}
        createLeadDefaults={{
          property: { id: property.id, name: property.name }
        }}
      />
    ),
    ["Applications"]: () => (
      <ApplicationsTableView propertyId={Number(propertyId)} />
    ),
    ["Showing rotation"]: () => (
      <ShowingRotation
        propertyId={property.id.toString()}
        showingType={
          (property.prescreening_template?.showing_type ??
            ShowingType.SelfGuided) as ShowingType
        }
      />
    ),
    ["Discount codes"]: () => <DiscountCodesTab />
  };

  return (
    <Layout
      footerClassName="p-4 border-t border-alpha-100"
      footer={
        <LayoutFooter
          onOpenInviteModal={() => setShowInviteApplicantsModal(true)}
          property={property}
        />
      }
      hideFooterForDesktop
      headerClassName="py-0"
      header={
        <LayoutHeader
          propertyName={propertyName}
          property={property}
          setShowInviteApplicantsModal={setShowInviteApplicantsModal}
          isSingleSyncSupported={isSingleSyncSupported}
          isSyncing={isSyncing}
          handleRequestSync={handleRequestSync}
          lastSuccessSyncedAt={syncData?.last_successful_sync}
          setShowActivityLog={setShowActivityLog}
        />
      }
    >
      <div>
        <Tabs<string>
          active={tab}
          onChange={(t) => setActiveTab(t)}
          tabs={tabs.map((t) => ({ value: t, label: t }))}
          tabStyle="line"
          size="sm"
          fullWidthTabs={false}
        />

        <div className="pt-4">{componentsMap[tab]()}</div>
      </div>

      <DeletePropertyModal
        opened={showDeletePropertyModal}
        onClose={() => setShowDeletePropertyModal(false)}
        propertyId={property.id.toString()}
        onSuccess={() => {
          navigate("/properties");
        }}
      />
      {showInviteApplicantsModal && propertyName && (
        <InviteAndCreateModal
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          propertyId={property.id}
          propertyName={propertyName}
          // unitId={undefined}
          onInviteRefreshKey={["properties", propertyId]}
        />
      )}

      <PropertyActivityLog
        showActivityLog={showActivityLog}
        setShowActivityLog={setShowActivityLog}
        propertyId={property.id.toString()}
      />
    </Layout>
  );
};
